// Заметка рекрутёра на карточке кандидата.
//
// Панель, а не поле прямо в карточке: заметка не правится и не удаляется — она дописывается.
// Это осознанно. Заметка про кандидата это след разговора («обещал перезвонить», «просил
// объект поближе к дому»), и правка задним числом превращает след в пересказ.

import type { FormEvent } from 'react';
import { useState } from 'react';

import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { TextArea } from '@/shared/ui/text-area';

import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { useAddNoteMutation } from '../queries';

const EDIT_PERMISSION = 'hiring.candidate.edit';

export function NotePanel({ candidateId, onClose }: { candidateId: string; onClose: () => void }) {
  const allowed = useCan(EDIT_PERMISSION);
  const мутация = useAddNoteMutation(candidateId);
  const [текст, setТекст] = useState('');
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [отказ, setОтказ] = useState<string | null>(null);

  function отправить(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setОтказ(
        'Писать заметки может рекрутёр и кадры. Попросите администратора выдать право на правку карточек.',
      );
      return;
    }
    if (мутация.isPending) {
      setОтказ('Заметка уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (текст.trim() === '') {
      setОшибка('Пустая заметка ничего не сохранит: напишите, что выяснилось.');
      return;
    }
    setОшибка(undefined);
    setОтказ(null);
    мутация.mutate(текст.trim(), { onSuccess: onClose });
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!allowed && <NoPermissionNote что='писать заметки' кто='Карточки ведут рекрутёр и кадры.' />}

      <TextArea
        id='candidate-note'
        label='Заметка'
        required
        rows={6}
        value={текст}
        onChange={setТекст}
        placeholder='Например: просит объект в Перми, готов выйти с 10 числа'
        error={ошибка}
        hint='Заметка не правится и не удаляется — она дописывается. Так и задумано: это след разговора, а не карточка.'
      />

      {мутация.isError && <ErrorAlert error={мутация.error} />}
      {отказ !== null && <GateNote>{отказ}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {мутация.isPending ? 'Сохраняем…' : 'Добавить заметку'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
